import { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  makeStyles,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Fade,
  Tooltip,
} from '@material-ui/core';
import { Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import UnsavedChangesModal from '../../../Shared/UnsavedChangesModal';
import FeaturesSection from './widgets/FeaturesSection/FeaturesSection';
import BasicInfoSection from './widgets/BasicInfoSection';
import ScheduledActivitiesSection from './widgets/ScheduledActivitiesSection';
import OtherAddressesSection from './widgets/OtherAddressesSection';
import CustomEmailSection from './widgets/CustomEmailSection/CustonEmailSection';
import CustomTagsSelector from './widgets/CustomTags';
import AddressForm from '../AddressForm';
import ClaimReportTypeSection from './widgets/ClaimReportTypeSection/ClaimReportTypeSection';
import { checkPermission } from '../../../../helpers/checkPermissions';
import permissions from '../../../../config/permissions';
import { setAccountBackendErrors } from '../../../../redux_store';
import InstantAssessment from '../../../InstantAssessment';
import { ACCOUNT_TABS, ACCOUNT_TAB_LABELS } from '../../constants';

const useStyles = makeStyles((theme) => ({
  checkbox: {
    margin: theme.spacing(0, 0, 2.25, 0),
  },
  dataContainer: {
    width: '100%',
    maxWidth: 1400,
    paddingTop: 10,
  },
  sectionContent: {
    width: '100%',
    maxWidth: 1000,
  },
  section: {
    width: '100%',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4),
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2.5),
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 8,
    },
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.colors.blue2,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.colors.lightBlue}`,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: theme.spacing(1),
    },
  },
  tabsContainer: {
    borderRadius: '0',
    border: 'none',
    boxShadow: 'none',
    marginBottom: 0,
    padding: '0 20px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 1,
      background: 'linear-gradient(90deg, rgba(0,168,255,0.2) 0%, rgba(0,168,255,0.1) 50%, rgba(0,168,255,0.2) 100%)',
    },
  },
  tabs: {
    minHeight: 54,
    paddingTop: 16,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTabs-flexContainer': {
      gap: '4px',
      alignItems: 'flex-end',
      paddingTop: 16,
      paddingLeft: 16,
      justifyContent: 'flex-start',
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.8rem',
      color: '#6b7280',
      padding: theme.spacing(1.5, 3),
      minWidth: 140,
      minHeight: 48,
      position: 'relative',
      borderRadius: '12px 12px 0 0',
      background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
      border: '1px solid #e5e7eb',
      borderBottom: 'none',
      marginBottom: 0,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 8,
        left: 12,
        right: 12,
        height: 2,
        borderRadius: 1,
        opacity: 0,
        transition: 'opacity 0.3s ease',
      },
      '&:hover': {
        color: '#374151',
        background: 'linear-gradient(145deg, #ffffff 0%, #f1f5f9 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 -4px 16px rgba(0, 168, 255, 0.12)',
        '&::before': {
          opacity: 1,
        },
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '0.75rem',
        padding: theme.spacing(1.25, 2.5),
        minWidth: 120,
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '0.7rem',
        padding: theme.spacing(1, 2),
        minWidth: 100,
      },
    },
    '& .MuiTab-root.Mui-selected': {
      color: theme.colors.blue2,
      background: 'linear-gradient(145deg, #ffffff 0%, #ffffff 100%)',
      borderColor: '#5B65DC',
      borderWidth: '2px 2px 0 2px',
      borderStyle: 'solid',
      zIndex: 2,
      transform: 'translateY(-1px)',
      boxShadow: '0 -6px 20px rgba(0, 168, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -1,
        left: -1,
        right: -1,
        height: 2,
        background: '#ffffff',
        zIndex: 1,
      },
    },
    '& .MuiTab-root.tab-error': {
      borderColor: '#dc3545 !important',
      color: '#dc3545 !important',
      background: 'linear-gradient(145deg, #fff5f5 0%, #fef2f2 100%) !important',
      '&::before': {
        opacity: '1 !important',
      },
      '&:hover': {
        background: 'linear-gradient(145deg, #fef2f2 0%, #fed7d7 100%) !important',
        boxShadow: '0 -4px 16px rgba(220, 53, 69, 0.15) !important',
      },
    },
    '& .MuiTab-root.tab-error.Mui-selected': {
      borderColor: '#dc3545 !important',
      color: '#dc3545 !important',
      background: 'linear-gradient(145deg, #ffffff 0%, #ffffff 100%) !important',
      boxShadow: '0 -6px 20px rgba(220, 53, 69, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.9) !important',
      '&::after': {
        background: '#ffffff !important',
      },
    },
    '& .MuiTab-root.tab-beta': {
      borderColor: '#ff8c00 !important',
      color: '#ff8c00 !important',
      background: 'linear-gradient(145deg, #fff8f0 0%, #fef5e7 100%) !important',
      position: 'relative',
      '&:hover': {
        background: 'linear-gradient(145deg, #fef5e7 0%, #fed7aa 100%) !important',
        boxShadow: '0 -4px 16px rgba(255, 140, 0, 0.15) !important',
      },
    },
    '& .MuiTab-root.tab-beta.Mui-selected': {
      borderColor: '#ff8c00 !important',
      color: '#ff8c00 !important',
      background: 'linear-gradient(145deg, #ffffff 0%, #ffffff 100%) !important',
      boxShadow: '0 -6px 20px rgba(255, 140, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.9) !important',
      '&::after': {
        background: '#ffffff !important',
      },
    },
    '& .MuiTab-root.tab-separated': {
      marginLeft: 'auto !important',
    },
  },
  tabPanel: {
    width: '100%',
    padding: 24,
  },
  tabPanelContent: {
    height: '100%',
    padding: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  fadeContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  tabPanelWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.15s ease-in-out, visibility 0.15s ease-in-out',
    '&.active': {
      opacity: 1,
      visibility: 'visible',
      position: 'relative',
    },
  },
  formCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: theme.spacing(12), // Space for actions
    margin: theme.spacing(3, 0),
    marginTop: 0,
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      borderRadius: 12,
    },
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  formContent: {
    width: '100%',
    maxHeight: '100%',
    height: '100%',
  },
  betaBadge: {
    position: 'absolute',
    top: -10,
    right: -18,
    color: '#ff8c00',
    border: '1px solid #ff8c00',
    opacity: .9,
    fontSize: '0.4rem',
    fontWeight: 700,
    padding: '3px 6px',
    borderRadius: 8,
    textTransform: 'uppercase',
    lineHeight: 1,
    letterSpacing: 1,
    zIndex: 10,
  },
  tabDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'auto', // Allow for tooltip
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiTab-wrapper': {
      color: 'rgba(0, 0, 0, 0.38) !important',
    },
    '&.Mui-selected': {
      color: 'rgba(0, 0, 0, 0.38) !important',
    },
  },
}));

// TabPanel component
function TabPanel(props) {
  const { children, value, index, classes, ...other } = props;

  return (
    <Fade in={value === index} timeout={300} unmountOnExit style={{ height: '100%' }}
          className={classes.tabPanel}>
      <div
        role="tabpanel"
        id={`account-tabpanel-${index}`}
        aria-labelledby={`account-tab-${index}`}
        className={classes.tabPanelContent}
        {...other}
      >
        {children}
      </div>
    </Fade>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  classes: PropTypes.object.isRequired,
};

function a11yProps(index) {
  return {
    id: `account-tab-${index}`,
    'aria-controls': `account-tabpanel-${index}`,
  };
}

const AccountForm = ({
                       values,
                       isEditMode,
                       isViewMode = false,
                       accountUuid,
                       setAccount,
                       surveysPayload,
                       resourcesPayload,
                       onTabChange,
                       setFieldValue,
                       setSubmitting,
                       editAccount,
                       dirty,
                       formikRef,
                       instantSurveysActive = true,
                     }) => {
  const classes = useStyles();
  const { en: { forms } } = useSelector((state) => state.i18n);
  const {
    metadata: { currentAccount, childAccountPermissions, currentAccountPermissions },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const [activeTab, setActiveTab] = useState(ACCOUNT_TABS.BASIC_INFORMATION);
  const [pendingTabIndex, setPendingTabIndex] = useState(null);
  const [showUnsavedChangesGuard, setShowUnsavedChangesGuard] = useState(false);
  const scrollableContentRef = useRef(null);
  const dispatch = useDispatch();
  const { backendErrors } = useSelector((state) => state.accountEditing);
  const setBackendErrors = (newErrors) => {
    dispatch(setAccountBackendErrors(newErrors));
  };
  const permissionsForShowingOptions = useMemo(
    () => ({
      canDisableOrders: checkPermission(
        childAccountPermissions,
        permissions.ACCOUNT_PATCH_DISABLE_ORDERS,
      ),
      canDisableAssessments: checkPermission(
        childAccountPermissions,
        permissions.ACCOUNT_PATCH_DISABLE_ASSESSMENTS,
      ),
      canDisableAllergiendResources: checkPermission(
        childAccountPermissions,
        permissions.ACCOUNT_PATCH_ALLERGIEND_RESOURCES,
      ),
      canEnableClaimsManagement: checkPermission(
        childAccountPermissions,
        permissions.ACCOUNT_PATCH_ENABLE_CLAIM_MANAGEMENT,
      ),
      canDisableResources: checkPermission(
        childAccountPermissions,
        permissions.ACCOUNT_PATCH_RESOURCE_ALL,
      ),
      canEnableImmunotherapyReport: checkPermission(
        childAccountPermissions,
        permissions.ACCOUNT_PATCH_DISABLE_ORDERS,
      ),
    }),
    [childAccountPermissions],
  );

  const {
    canDisableAssessments,
    canDisableAllergiendResources,
    canDisableResources,
    canDisableOrders,
    canEnableClaimsManagement,
    canEnableImmunotherapyReport,
  } = permissionsForShowingOptions;

  const isAnyDisableFeaturesAvailable = canDisableAssessments
    || canDisableAllergiendResources
    || canDisableResources;

  const isAnyEnableFeaturesAvailable = canDisableOrders
    || canEnableClaimsManagement
    || canEnableImmunotherapyReport;

  const isAnyFeaturesAvailable =
    isAnyDisableFeaturesAvailable || isAnyEnableFeaturesAvailable;

  // Check permission for Instant Assessments tab
  const hasInstantAssessmentsPermission = values?.accountType !== "distributionSponsor" && !isViewMode && checkPermission(
    childAccountPermissions,
    permissions.MANAGE_INSTANT_SURVEY_ASSESSMENTS,
  );

  const isCurrentAccountEditing = currentAccount.uuid === accountUuid;

  // Function to clear backend error for a specific field
  const clearBackendError = (fieldName) => {
    if (setBackendErrors && backendErrors && backendErrors[fieldName]) {
      const updatedErrors = { ...backendErrors };
      delete updatedErrors[fieldName];
      setBackendErrors(updatedErrors);
    }
  };

  // Function to check if tab has errors
  const getTabErrors = (tabName) => {
    if (!backendErrors) return [];

    const errorKeys = Object.keys(backendErrors).filter(key => backendErrors[key]);

    switch (tabName) {
      case ACCOUNT_TABS.BASIC_INFORMATION:
        return errorKeys.filter(key => [
          'businessName', 'tradeName', 'phone', 'fax', 'contactName',
          'headProviderName', 'customIdentifier', 'scheduleDeliverTime',
          'customTags',
        ].includes(key));

      case ACCOUNT_TABS.ADDRESSES:
        return errorKeys.filter(key => [
          'organizationAddress', 'shippingAddress', 'otherAddresses',
        ].some(prefix => key.startsWith(prefix)));

      case ACCOUNT_TABS.FEATURES:
        return errorKeys.filter(key => [
          'disableAssessments', 'disableOrders', 'allowedSurveys',
          'disableResourcesAll', 'allowedResources', 'claimReportType',
          'ispConfiguration',
        ].some(prefix => key.startsWith(prefix)));

      case ACCOUNT_TABS.SETTINGS:
        return errorKeys.filter(key => [
          'customEmailSection',
        ].some(prefix => key.startsWith(prefix)));

      case ACCOUNT_TABS.INSTANT_ASSESSMENTS:
        return errorKeys.filter(key => [
          'instantSurveys', 'instantAssessments',
        ].some(prefix => key.startsWith(prefix)));

      default:
        return [];
    }
  };

  // Helper to get available tabs based on permissions
  const getAvailableTabs = () => {
    const tabs = [];
    tabs.push(ACCOUNT_TABS.BASIC_INFORMATION);
    tabs.push(ACCOUNT_TABS.ADDRESSES);

    if (isDistributionSponsor || isAnyFeaturesAvailable) {
      tabs.push(ACCOUNT_TABS.FEATURES);
    }

    if (isDistributionSponsor) {
      tabs.push(ACCOUNT_TABS.SETTINGS);
    }

    if (hasInstantAssessmentsPermission) {
      tabs.push(ACCOUNT_TABS.INSTANT_ASSESSMENTS);
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  const handleTabChange = (event, newValue) => {
    const tabName = availableTabs[newValue];

    // Check if trying to navigate to disabled or non-existent Instant Assessments tab
    if (tabName === ACCOUNT_TABS.INSTANT_ASSESSMENTS && (!hasInstantAssessmentsPermission || !instantSurveysActive)) {
      return; // Prevent navigation to disabled or unauthorized tab
    }

    // Check if we're navigating to Instant Assessments tab and have unsaved changes
    if (tabName === ACCOUNT_TABS.INSTANT_ASSESSMENTS && dirty && activeTab !== ACCOUNT_TABS.INSTANT_ASSESSMENTS) {
      setPendingTabIndex(tabName);
      setShowUnsavedChangesGuard(true);
      return;
    }

    // Allow normal navigation for other tabs
    performTabChange(tabName);
  };

  const performTabChange = (tabName) => {
    setActiveTab(tabName);

    // Reset scroll position to top instantly
    if (scrollableContentRef.current) {
      scrollableContentRef.current.scrollTo({
        top: 0,
        behavior: 'auto', // No animation for instant scroll
      });
    }

    // Notify parent about tab change
    if (onTabChange) {
      onTabChange(tabName);
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesGuard(false);

    // Reset form to original values
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }

    if (pendingTabIndex !== null) {
      performTabChange(pendingTabIndex);
      setPendingTabIndex(null);
    }
  };

  const handleKeepEditing = () => {
    setShowUnsavedChangesGuard(false);
    setPendingTabIndex(null);
  };

  return <>
    {/* Tabs Navigation */}
    <div className={classes.tabsContainer}>
      <Tabs
        value={availableTabs.indexOf(activeTab)}
        onChange={handleTabChange}
        className={classes.tabs}
        variant="standard"
      >
        <Tab
          label={ACCOUNT_TAB_LABELS[ACCOUNT_TABS.BASIC_INFORMATION]}
          {...a11yProps(0)}
          className={getTabErrors(ACCOUNT_TABS.BASIC_INFORMATION).length > 0 ? 'tab-error' : ''}
        />
        <Tab
          label={ACCOUNT_TAB_LABELS[ACCOUNT_TABS.ADDRESSES]}
          {...a11yProps(1)}
          className={getTabErrors(ACCOUNT_TABS.ADDRESSES).length > 0 ? 'tab-error' : ''}
        />
        {(isDistributionSponsor || isAnyFeaturesAvailable) &&
          <Tab
            label={ACCOUNT_TAB_LABELS[ACCOUNT_TABS.FEATURES]}
            {...a11yProps(2)}
            className={getTabErrors(ACCOUNT_TABS.FEATURES).length > 0 ? 'tab-error' : ''}
          />}
        {isDistributionSponsor &&
          <Tab
            label={ACCOUNT_TAB_LABELS[ACCOUNT_TABS.SETTINGS]}
            {...a11yProps(3)}
            className={getTabErrors(ACCOUNT_TABS.SETTINGS).length > 0 ? 'tab-error' : ''}
          />}
        {hasInstantAssessmentsPermission &&
          <Tab
            label={
              <Tooltip
                title={!instantSurveysActive ? 'Available after saving the account' : ''}
                arrow
                placement="top"
              >
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  {ACCOUNT_TAB_LABELS[ACCOUNT_TABS.INSTANT_ASSESSMENTS]}
                  {/*<span className={classes.betaBadge}>BETA</span>*/}
                </div>

              </Tooltip>
            }
            {...a11yProps(4)}
            className={`tab-beta tab-separated ${getTabErrors(ACCOUNT_TABS.INSTANT_ASSESSMENTS).length > 0 ? 'tab-error' : ''} ${!instantSurveysActive ? classes.tabDisabled : ''}`}
          />}
      </Tabs>
    </div>

    <div className={classes.formCard} style={{
      marginBottom: activeTab === ACCOUNT_TABS.INSTANT_ASSESSMENTS ? 0 : null, // Space for actions
    }}>
      <div className={classes.scrollableContent} ref={scrollableContentRef}>
        <div className={classes.formContent}>
          <Form style={{ maxHeight: '100%', height: '100%' }}>
            <Grid container direction="column" alignItems="flex-start"
                  style={{ maxHeight: '100%', height: '100%' }}>
              {/* Tab Panels */}
              <div className={classes.fadeContainer}>
                {/* All TabPanels are rendered but only one is visible with fade effect */}
                <TabPanel value={activeTab} index={ACCOUNT_TABS.BASIC_INFORMATION} classes={classes}>
                  {/* Account Type Section */}
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      {forms.accountType}
                    </Typography>
                    <Grid container className={classes.dataContainer}>
                      <Grid item xs={12} md={6} lg={4} container>
                        <Field name="customTags" type="selector">
                          {({ form }) => {
                            return <CustomTagsSelector
                              values={values}
                              backendErrors={backendErrors}
                              form={form}
                              disabled={isViewMode}
                              clearBackendError={clearBackendError}
                            />;
                          }}
                        </Field>
                      </Grid>
                    </Grid>
                  </div>

                  {/* Basic Information Section */}
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      {forms.basicInfo}
                    </Typography>
                    <div className={classes.sectionContent}>
                      <BasicInfoSection
                        values={values}
                        backendErrors={backendErrors}
                        isEditMode={isEditMode}
                        isCurrentAccountEditing={isCurrentAccountEditing}
                        isDistributionSponsor={isDistributionSponsor}
                        clearBackendError={clearBackendError}
                        disabled={isViewMode}
                      />
                    </div>
                  </div>

                  {/* Scheduled Activities Section */}
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      {forms.scheduledActivities}
                    </Typography>
                    <div className={classes.sectionContent}>
                      <ScheduledActivitiesSection
                        scheduleDeliverTime={values.scheduleDeliverTime}
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value={activeTab} index={ACCOUNT_TABS.ADDRESSES} classes={classes}>
                  {/* Organization Address Section */}
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      {forms.organizationAddress}
                    </Typography>
                    <div className={classes.sectionContent}>
                      <AddressForm
                        prefix="organizationAddress"
                        backendErrors={backendErrors}
                        disabled={isViewMode}
                        clearBackendError={clearBackendError}
                      />
                    </div>
                  </div>

                  {/* Shipping Address Section */}
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      {forms.shippingAddress}
                    </Typography>
                    <div className={classes.sectionContent}>
                      <Field name="isShippingTheSame" type="checkbox">
                        {({ field, form }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                name={field.name}
                                checked={field.value}
                                color="primary"
                                disabled={isViewMode}
                                onChange={(e, checked) => {
                                  form.setFieldValue(field.name, checked);
                                }}
                              />
                            }
                            className={classes.checkbox}
                            label={forms.useSameAddress}
                          />
                        )}
                      </Field>
                      <AddressForm
                        prefix="shippingAddress"
                        backendErrors={values.isShippingTheSame ? undefined : backendErrors}
                        disabled={isViewMode || values.isShippingTheSame}
                        clearBackendError={clearBackendError}
                      />
                    </div>
                  </div>

                  {/* Other Addresses Section */}
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      {forms.otherAddresses}
                    </Typography>
                    <div className={classes.sectionContent}>
                      <OtherAddressesSection
                        otherAddresses={values.otherAddresses}
                        disabled={isViewMode}
                        clearBackendError={clearBackendError}
                      />
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value={activeTab} index={ACCOUNT_TABS.FEATURES} classes={classes}>
                  {/* Features Section */}
                  {isAnyFeaturesAvailable &&
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        {forms.features}
                      </Typography>
                      <div className={classes.sectionContent}>
                        <FeaturesSection
                          canDisableOrders={canDisableOrders}
                          canDisableAllergiendResources={canDisableAllergiendResources}
                          canEnableClaimsManagement={canEnableClaimsManagement}
                          canDisableAssessments={canDisableAssessments}
                          canEnableImmunotherapyReport={canEnableImmunotherapyReport}
                          canDisableResources={canDisableResources}
                          clearBackendError={clearBackendError}
                          backendErrors={backendErrors}
                          surveysPayload={surveysPayload}
                          resourcesPayload={resourcesPayload}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  }
                  {isDistributionSponsor && (
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        {forms.claimReportType || 'Claim Report Type'}
                      </Typography>
                      <div className={classes.sectionContent}>
                        <ClaimReportTypeSection
                          values={values}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={ACCOUNT_TABS.SETTINGS} classes={classes}>
                  {/* Custom Email Section */}
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      {forms.customEmailSettings || 'Email & SMS Settings'}
                    </Typography>
                    <div className={classes.sectionContent}>
                      <Field name="customEmailSection" type="checkbox">
                        {({ form }) => (
                          <CustomEmailSection
                            backendErrors={backendErrors}
                            form={form}
                            isDistributionSponsor={isDistributionSponsor}
                            setAccount={setAccount}
                            disabled={isViewMode}
                            clearBackendError={clearBackendError}
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                </TabPanel>

                {hasInstantAssessmentsPermission &&
                  <TabPanel value={activeTab} index={ACCOUNT_TABS.INSTANT_ASSESSMENTS} classes={classes}>
                    {/* Instant Assessments Section */}
                    <div style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      padding: 0,
                      margin: 0,
                      overflow: 'hidden',
                    }}>
                      <InstantAssessment
                        values={values}
                        setFieldValue={setFieldValue}
                        setSubmitting={setSubmitting}
                        editAccount={editAccount}
                      />
                    </div>
                  </TabPanel>
                }
              </div>
            </Grid>
          </Form>
        </div>
      </div>
    </div>

    {/* Unsaved Changes Modal */}
    <UnsavedChangesModal
      open={showUnsavedChangesGuard}
      title="Unsaved Changes"
      message="You have unsaved changes. Are you sure you want to switch to the Instant Assessments tab? Your changes will be lost."
      discardButtonText="Switch Tab"
      keepEditingButtonText="Keep Editing"
      onDiscard={handleDiscardChanges}
      onKeepEditing={handleKeepEditing}
    />
  </>;
};

AccountForm.propTypes = {
  values: PropTypes.shape({
    otherAddresses: PropTypes.array,
    isShippingTheSame: PropTypes.bool,
    tradeName: PropTypes.string,
    businessName: PropTypes.string,
    scheduleDeliverTime: PropTypes.string,
  }).isRequired,
  sectionClassName: PropTypes.string,
  sectionTitleClassName: PropTypes.string,
  backendErrors: PropTypes.shape({
    businessName: PropTypes.string,
    tradeName: PropTypes.string,
    phone: PropTypes.string,
    fax: PropTypes.string,
    contactName: PropTypes.string,
    headProviderName: PropTypes.string,
    customIdentifier: PropTypes.string,
  }),
  isEditMode: PropTypes.bool,
  isViewMode: PropTypes.bool,
  accountUuid: PropTypes.string.isRequired,
  setBackendErrors: PropTypes.func,
  onTabChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  setSubmitting: PropTypes.func,
  dirty: PropTypes.bool,
  formikRef: PropTypes.object,
};

AccountForm.defaultProps = {
  backendErrors: undefined,
  isEditMode: false,
  isViewMode: false,
};

export default AccountForm;
